import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import api from '../api';
import { showError, showSuccess } from '../utils/errorHandler';

const LoanDocuments = ({ loanId, isAdmin = false }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('modelo_600');
  const [description, setDescription] = useState('');
  const { t } = useTranslation();
  const { user } = useAuth();

  useEffect(() => {
    if (loanId) {
      fetchDocuments();
    }
  }, [loanId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDocuments = async () => {
    try {
      const response = await api.get(`/api/loans/${loanId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        showError({ message: t('documents.onlyPdfImages') });
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showError({ message: 'L\'arxiu no pot superar 10MB' });
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showError({ message: 'Selecciona un arxiu primer' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);
      formData.append('description', description);

      await api.post(`/api/admin/loans/${loanId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      showSuccess(t('documents.uploaded'));
      setSelectedFile(null);
      setDescription('');
      document.getElementById('file-input').value = '';
      fetchDocuments();
    } catch (error) {
      showError(error, 'Error en pujar document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await api.get(`/api/loans/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError(error, 'Error en descarregar document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm(t('documents.confirmDelete'))) return;

    try {
      await api.delete(`/api/admin/loans/documents/${documentId}`);
      showSuccess(t('documents.deleted'));
      fetchDocuments();
    } catch (error) {
      showError(error, 'Error en eliminar document');
    }
  };

  const getDocumentIcon = (type) => {
    const icons = {
      'modelo_600': '📄',
      'transferencia': '💸',
      'recepcion': '✅',
      'otros': '📎'
    };
    return icons[type] || '📎';
  };

  const getDocumentLabel = (type) => {
    const labels = {
      'modelo_600': t('documents.model600'),
      'transferencia': t('documents.transfer'),
      'recepcion': t('documents.receipt'),
      'otros': t('documents.other')
    };
    return labels[type] || t('documents.document');
  };

  return (
    <div className="dark-card" style={{ padding: '1.5rem', marginTop: '1.5rem' }}>
      <h3 style={{ 
        fontSize: 'clamp(1rem, 2vw, 1.05rem)', 
        fontWeight: '600', 
        color: 'white', 
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span>📁</span>
        <span>{t('documents.title')}</span>
      </h3>

      {/* Admin Upload Section */}
      {isAdmin && (
        <div style={{ 
          padding: '1.5rem', 
          background: 'rgba(255,255,255,0.03)', 
          borderRadius: '12px',
          marginBottom: '1.5rem',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h4 style={{ fontSize: '0.9375rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', marginBottom: '1rem' }}>
            📤 Pujar Document
          </h4>
          
          <div style={{ display: 'grid', gap: '1rem' }}>
            {/* Document Type Selector */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>
                Tipus de Document
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.875rem'
                }}
              >
                <option value="modelo_600">{t('documents.model600')}</option>
                <option value="transferencia">{t('documents.transfer')}</option>
                <option value="recepcion">{t('documents.receipt')}</option>
                <option value="otros">{t('documents.other')}</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>
                Descripció (opcional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Model 600 presentat gener 2026"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            {/* File Input */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>
                Arxiu (PDF o Imatge, màx 10MB)
              </label>
              <input
                id="file-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.875rem'
                }}
              />
              {selectedFile && (
                <p style={{ fontSize: '0.8125rem', color: '#10b981', marginTop: '0.5rem' }}>
                  ✓ Seleccionat: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              style={{
                padding: '0.875rem 1.5rem',
                borderRadius: '10px',
                background: uploading ? 'rgba(107,114,128,0.3)' : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                color: 'white',
                border: 'none',
                fontWeight: '600',
                fontSize: '0.875rem',
                cursor: uploading || !selectedFile ? 'not-allowed' : 'pointer',
                opacity: uploading || !selectedFile ? 0.5 : 1
              }}
            >
              {uploading ? '⏳ Pujant...' : '📤 Pujar Document'}
            </button>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div>
        {documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.5)' }}>
            <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📂</p>
            <p style={{ fontSize: '0.875rem' }}>{t('documents.noDocuments')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {documents.map((doc) => (
              <div
                key={doc._id}
                style={{
                  padding: '1rem',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'background 0.2s ease',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              >
                <div style={{ marginBottom: '0.75rem', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>{getDocumentIcon(doc.document_type)}</span>
                    <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                      <p style={{ 
                        fontSize: '0.8rem', 
                        fontWeight: '600', 
                        color: 'white',
                        marginBottom: '0.25rem',
                        wordBreak: 'break-all'
                      }}>
                        {doc.filename}
                      </p>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: 'rgba(255,255,255,0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          <span>🏷️</span>
                          {getDocumentLabel(doc.document_type)}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                          {(doc.file_size / 1024).toFixed(0)} KB
                        </span>
                        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                          {new Date(doc.uploaded_at).toLocaleDateString('ca-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      </div>
                      {doc.description && (
                        <p style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                          {doc.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleDownload(doc._id, doc.filename)}
                    style={{
                      padding: '0.4rem 0.75rem',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                      color: 'white',
                      border: 'none',
                      fontWeight: '600',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    📥 {t('misc.download')}
                  </button>
                  
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(doc._id)}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        background: 'rgba(239,68,68,0.1)',
                        color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.3)',
                        fontWeight: '600',
                        fontSize: '0.8125rem',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanDocuments;
